import React, { useState, useEffect, useRef } from 'react';

export default function ThreeQubitSystem() {
  const [qubits, setQubits] = useState([
    { id: 1, measured: false, state: null, entangled: true },
    { id: 2, measured: false, state: null, entangled: true },
    { id: 3, measured: false, state: null, entangled: false }
  ]);
  const [rotation, setRotation] = useState(0);
  // wavePhase was removed (unused)
  const canvasRefs = useRef([]);

  const allMeasured = qubits.every(q => q.measured);
  const entangledMeasured = qubits.filter(q => q.entangled).every(q => q.measured);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => (prev + 3) % 360);
    }, 50); // Reduced frequency for better performance
    return () => clearInterval(interval);
  }, []);

  // Draw Bloch spheres
  useEffect(() => {
    qubits.forEach((qubit, idx) => {
      const canvas = canvasRefs.current[idx];
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = 60;

      ctx.clearRect(0, 0, width, height);

      // Draw sphere
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      if (qubit.entangled) {
        gradient.addColorStop(0, 'rgba(92, 45, 145, 0.12)');
        gradient.addColorStop(1, 'rgba(219, 39, 119, 0.06)');
      } else {
        gradient.addColorStop(0, 'rgba(100, 100, 100, 0.12)');
        gradient.addColorStop(1, 'rgba(60, 60, 60, 0.04)');
      }
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();

      // Sphere outline
      ctx.strokeStyle = qubit.entangled ? 'rgba(92, 45, 145, 0.6)' : 'rgba(120, 120, 120, 0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw axes
      ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);

      // Z axis
      ctx.beginPath();
      ctx.moveTo(centerX, centerY - radius);
      ctx.lineTo(centerX, centerY + radius);
      ctx.stroke();

      // X axis
      ctx.beginPath();
      ctx.moveTo(centerX - radius, centerY);
      ctx.lineTo(centerX + radius, centerY);
      ctx.stroke();

      ctx.setLineDash([]);

      // Labels
      ctx.fillStyle = 'rgba(8, 145, 178, 0.9)';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('|0⟩', centerX - 12, centerY - radius - 8);

      ctx.fillStyle = 'rgba(219, 39, 119, 0.9)';
      ctx.fillText('|1⟩', centerX - 12, centerY + radius + 20);

      if (!qubit.measured) {
        if (qubit.entangled) {
          // Entangled qubits - rotating superposition
          const theta = Math.PI / 4;
          const x = radius * Math.sin(theta) * Math.cos(rotation * Math.PI / 180);
          const z = -radius * Math.cos(theta);

          // State vector
          ctx.strokeStyle = 'rgba(217, 119, 6, 1)';
          ctx.fillStyle = 'rgba(217, 119, 6, 1)';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(centerX + x, centerY + z);
          ctx.stroke();

          // Arrow head
          const angle = Math.atan2(z, x);
          const headLen = 8;
          ctx.beginPath();
          ctx.moveTo(centerX + x, centerY + z);
          ctx.lineTo(
            centerX + x - headLen * Math.cos(angle - Math.PI / 6),
            centerY + z - headLen * Math.sin(angle - Math.PI / 6)
          );
          ctx.moveTo(centerX + x, centerY + z);
          ctx.lineTo(
            centerX + x - headLen * Math.cos(angle + Math.PI / 6),
            centerY + z - headLen * Math.sin(angle + Math.PI / 6)
          );
          ctx.stroke();

          // Glow effect
          ctx.shadowBlur = 15;
          ctx.shadowColor = 'rgba(217, 119, 6, 0.5)';
          ctx.beginPath();
          ctx.arc(centerX + x, centerY + z, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          // Independent qubit - different rotation
          const theta = Math.PI / 3;
          const x = radius * Math.sin(theta) * Math.cos(-rotation * 1.5 * Math.PI / 180);
          const z = -radius * Math.cos(theta);

          // State vector
          ctx.strokeStyle = 'rgba(107, 114, 128, 1)';
          ctx.fillStyle = 'rgba(107, 114, 128, 1)';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(centerX + x, centerY + z);
          ctx.stroke();

          // Arrow head
          const angle = Math.atan2(z, x);
          const headLen = 8;
          ctx.beginPath();
          ctx.moveTo(centerX + x, centerY + z);
          ctx.lineTo(
            centerX + x - headLen * Math.cos(angle - Math.PI / 6),
            centerY + z - headLen * Math.sin(angle - Math.PI / 6)
          );
          ctx.moveTo(centerX + x, centerY + z);
          ctx.lineTo(
            centerX + x - headLen * Math.cos(angle + Math.PI / 6),
            centerY + z - headLen * Math.sin(angle + Math.PI / 6)
          );
          ctx.stroke();
        }
      } else {
        // Collapsed state
        const finalZ = qubit.state === 0 ? -radius : radius;
        ctx.strokeStyle = qubit.state === 0 ? 'rgba(8, 145, 178, 1)' : 'rgba(219, 39, 119, 1)';
        ctx.fillStyle = qubit.state === 0 ? 'rgba(8, 145, 178, 1)' : 'rgba(219, 39, 119, 1)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(centerX, centerY + finalZ);
        ctx.stroke();

        // Arrow head
        const headLen = 10;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY + finalZ);
        ctx.lineTo(centerX - headLen / 2, centerY + finalZ + (qubit.state === 0 ? headLen : -headLen));
        ctx.lineTo(centerX + headLen / 2, centerY + finalZ + (qubit.state === 0 ? headLen : -headLen));
        ctx.closePath();
        ctx.fill();
      }
    });
  }, [qubits, rotation]);

  const handleMeasureQubit = (qubitId) => {
    const qubit = qubits.find(q => q.id === qubitId);
    if (qubit.measured) return;

    if (qubit.entangled) {
      // Measuring an entangled qubit affects its partner
      const outcome = Math.random() < 0.5 ? 0 : 1;
      setQubits(qubits.map(q => {
        if (q.entangled) {
          return { ...q, measured: true, state: q.id === 1 ? outcome : 1 - outcome };
        }
        return q;
      }));
    } else {
      // Independent qubit measures independently
      const outcome = Math.random() < 0.5 ? 0 : 1;
      setQubits(qubits.map(q =>
        q.id === qubitId ? { ...q, measured: true, state: outcome } : q
      ));
    }
  };

  const handleReset = () => {
    setQubits([
      { id: 1, measured: false, state: null, entangled: true },
      { id: 2, measured: false, state: null, entangled: true },
      { id: 3, measured: false, state: null, entangled: false }
    ]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            3-Qubit System
          </h1>
          <p className="text-lg text-gray-600 mb-1">2 Entangled + 1 Independent</p>
          <p className="text-sm text-gray-500">
            {allMeasured ? '⚡ All Measurements Complete' : '🌌 Qubits in Superposition'}
          </p>
        </div>

        {/* System Diagram */}
        <div className="mb-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="text-gray-800 font-bold text-lg mb-3 text-center">System Configuration</h3>
          <div className="flex justify-center items-center gap-4 text-white text-sm">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center font-bold border-2 border-purple-300 text-xs">
                Q1
              </div>
              <div className="text-purple-600 text-2xl">⟷</div>
              <div className="w-12 h-12 rounded-full bg-pink-600 flex items-center justify-center font-bold border-2 border-pink-300 text-xs">
                Q2
              </div>
            </div>
            <div className="text-gray-300 text-3xl px-4">|</div>
            <div className="w-12 h-12 rounded-full bg-gray-500 flex items-center justify-center font-bold border-2 border-gray-300 text-xs">
              Q3
            </div>
          </div>
          <div className="flex justify-center gap-24 mt-2 text-xs">
            <div className="text-purple-700 font-semibold">Entangled Pair</div>
            <div className="text-gray-500 font-semibold">Independent</div>
          </div>
        </div>

        {/* Qubits Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Qubit 1 */}
          <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-purple-700">Qubit 1</h3>
              <span className="px-2 py-0.5 bg-purple-100 rounded-full text-purple-700 text-xs font-semibold">
                Entangled
              </span>
            </div>

            <div className="flex justify-center mb-2">
              <canvas ref={el => (canvasRefs.current[0] = el)} width={120} height={120} />
            </div>

            <div className="text-center space-y-1">
              <div className="text-gray-600 text-xs">
                |ψ₁₂⟩ = α|00⟩ + β|11⟩
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="bg-cyan-50 rounded p-1 border border-cyan-100">
                  <div className="text-cyan-600 text-xs">P(|0⟩)</div>
                  <div className="text-cyan-700 font-bold text-sm">50%</div>
                </div>
                <div className="bg-pink-50 rounded p-1 border border-pink-100">
                  <div className="text-pink-600 text-xs">P(|1⟩)</div>
                  <div className="text-pink-700 font-bold text-sm">50%</div>
                </div>
              </div>

              {!qubits[0].measured && (
                <button
                  onClick={() => handleMeasureQubit(1)}
                  className="w-full mt-2 px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-lg shadow-sm transform hover:scale-105 transition-all duration-200"
                >
                  Measure Q1
                </button>
              )}

              {qubits[0].measured && (
                <div className="mt-2 bg-purple-50 rounded-lg p-2 border border-purple-100">
                  <div className="text-gray-500 text-xs mb-1">Result</div>
                  <div className={`text-2xl font-bold ${qubits[0].state === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                    {qubits[0].state}
                  </div>
                  <div className="text-purple-600 text-xs">State: |{qubits[0].state}⟩</div>
                </div>
              )}
            </div>
          </div>

          {/* Qubit 2 */}
          <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-pink-700">Qubit 2</h3>
              <span className="px-2 py-0.5 bg-pink-100 rounded-full text-pink-700 text-xs font-semibold">
                Entangled
              </span>
            </div>

            <div className="flex justify-center mb-2">
              <canvas ref={el => (canvasRefs.current[1] = el)} width={120} height={120} />
            </div>

            <div className="text-center space-y-1">
              <div className="text-gray-600 text-xs">
                |ψ₁₂⟩ = α|00⟩ + β|11⟩
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="bg-cyan-50 rounded p-1 border border-cyan-100">
                  <div className="text-cyan-600 text-xs">P(|0⟩)</div>
                  <div className="text-cyan-700 font-bold text-sm">50%</div>
                </div>
                <div className="bg-pink-50 rounded p-1 border border-pink-100">
                  <div className="text-pink-600 text-xs">P(|1⟩)</div>
                  <div className="text-pink-700 font-bold text-sm">50%</div>
                </div>
              </div>

              {!qubits[1].measured && (
                <button
                  onClick={() => handleMeasureQubit(2)}
                  className="w-full mt-2 px-2 py-1.5 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-lg shadow-sm transform hover:scale-105 transition-all duration-200"
                >
                  Measure Q2
                </button>
              )}

              {qubits[1].measured && (
                <div className="mt-2 bg-pink-50 rounded-lg p-2 border border-pink-100">
                  <div className="text-gray-500 text-xs mb-1">Result</div>
                  <div className={`text-2xl font-bold ${qubits[1].state === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                    {qubits[1].state}
                  </div>
                  <div className="text-pink-600 text-xs">State: |{qubits[1].state}⟩</div>
                </div>
              )}
            </div>
          </div>

          {/* Qubit 3 */}
          <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-700">Qubit 3</h3>
              <span className="px-2 py-0.5 bg-gray-100 rounded-full text-gray-600 text-xs font-semibold">
                Independent
              </span>
            </div>

            <div className="flex justify-center mb-2">
              <canvas ref={el => (canvasRefs.current[2] = el)} width={120} height={120} />
            </div>

            <div className="text-center space-y-1">
              <div className="text-gray-600 text-xs">
                |ψ₃⟩ = α|0⟩ + β|1⟩
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="bg-cyan-50 rounded p-1 border border-cyan-100">
                  <div className="text-cyan-600 text-xs">P(|0⟩)</div>
                  <div className="text-cyan-700 font-bold text-sm">50%</div>
                </div>
                <div className="bg-pink-50 rounded p-1 border border-pink-100">
                  <div className="text-pink-600 text-xs">P(|1⟩)</div>
                  <div className="text-pink-700 font-bold text-sm">50%</div>
                </div>
              </div>

              {!qubits[2].measured && (
                <button
                  onClick={() => handleMeasureQubit(3)}
                  className="w-full mt-2 px-2 py-1.5 bg-gray-600 hover:bg-gray-700 text-white font-bold text-xs rounded-lg shadow-sm transform hover:scale-105 transition-all duration-200"
                >
                  Measure Q3
                </button>
              )}

              {qubits[2].measured && (
                <div className="mt-2 bg-gray-50 rounded-lg p-2 border border-gray-200">
                  <div className="text-gray-500 text-xs mb-1">Result</div>
                  <div className={`text-2xl font-bold ${qubits[2].state === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                    {qubits[2].state}
                  </div>
                  <div className="text-gray-500 text-xs">State: |{qubits[2].state}⟩</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Behavior Explanation */}
        {entangledMeasured && !qubits[2].measured && (
          <div className="mb-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h3 className="text-amber-800 font-bold text-sm mb-2 text-center">
              ⚠️ Notice: Entangled vs Independent Behavior
            </h3>
            <p className="text-amber-800 text-center text-sm">
              Q1 and Q2 collapsed together to opposite states ({qubits[0].state} and {qubits[1].state}).
              But Q3 is still in superposition! It's completely independent and unaffected by the measurement of Q1 and Q2.
            </p>
          </div>
        )}

        {/* Results Summary */}
        {allMeasured && (
          <div className="mb-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-gray-800 font-bold text-lg mb-3 text-center">Complete System State</h3>
            <div className="flex justify-center items-center gap-4 text-sm">
              <div className="text-center">
                <div className="text-purple-600 text-xs mb-1">Entangled Pair</div>
                <div className="flex items-center gap-2">
                  <div className={`text-2xl font-bold ${qubits[0].state === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                    {qubits[0].state}
                  </div>
                  <div className="text-purple-500 text-lg">⟷</div>
                  <div className={`text-2xl font-bold ${qubits[1].state === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                    {qubits[1].state}
                  </div>
                </div>
                <div className="text-purple-600 text-xs mt-1">
                  {qubits[0].state !== qubits[1].state ? '✓ Anti-correlated' : '✗ Error'}
                </div>
              </div>

              <div className="text-gray-300 text-2xl">|</div>

              <div className="text-center">
                <div className="text-gray-500 text-xs mb-1">Independent</div>
                <div className={`text-2xl font-bold ${qubits[2].state === 0 ? 'text-cyan-600' : 'text-pink-600'}`}>
                  {qubits[2].state}
                </div>
                <div className="text-gray-500 text-xs mt-1">Random result</div>
              </div>
            </div>
          </div>
        )}

        {/* Control Button */}
        <div className="flex justify-center mb-4">
          <button
            onClick={handleReset}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-md transform hover:scale-105 transition-all duration-200"
          >
            ↻ Reset All Qubits
          </button>
        </div>

        {/* Educational Info */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="font-bold text-lg mb-2 text-gray-900">
            🧬 Key Behaviors
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-gray-600 text-sm">
            <div>
              <h4 className="text-purple-700 font-semibold mb-1">Entangled Qubits (Q1 & Q2):</h4>
              <ul className="space-y-1 text-xs">
                <li>• Share the same wavefunction: |ψ₁₂⟩ = (|01⟩ - |10⟩)/√2</li>
                <li>• Rotate <strong>synchronously</strong> on their Bloch spheres</li>
                <li>• Measuring one <strong>instantly</strong> determines the other</li>
                <li>• Always produce <strong>opposite</strong> results (anti-correlated)</li>
                <li>• Cannot be described independently</li>
              </ul>
            </div>
            <div>
              <h4 className="text-gray-700 font-semibold mb-1">Independent Qubit (Q3):</h4>
              <ul className="space-y-1 text-xs">
                <li>• Has its own separate wavefunction: |ψ₃⟩</li>
                <li>• Rotates at a <strong>different speed</strong> on Bloch sphere</li>
                <li>• <strong>NOT affected</strong> by measurements of Q1 or Q2</li>
                <li>• Produces <strong>random</strong> results (no correlation)</li>
                <li>• Can be described completely on its own</li>
              </ul>
            </div>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-blue-800 text-xs text-center">
              <strong>Key Insight:</strong> Entanglement is a special quantum connection that must be created.
              By default, qubits are independent. Notice how Q1 and Q2 behave as a single system,
              while Q3 maintains its independence!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
